import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { type AppError, request, staffKeys } from '@/shared/api';

import { useStaffTokenStore } from '../model/useStaffTokenStore';
import type { StaffUser } from '../model/types';
import { staffUserSchema, toStaffUser } from './schema';

/**
 * Who is signed in, straight from the server. A stale token in localStorage is
 * not a session — only `/staff/me` answering is, which is why the panel waits
 * for this rather than trusting what it stored (CLAUDE.md §3.8).
 */
export function useStaffSession(): UseQueryResult<StaffUser, AppError> {
  const token = useStaffTokenStore((state) => state.token);

  return useQuery<StaffUser, AppError>({
    queryKey: staffKeys.me(),
    queryFn: async ({ signal }): Promise<StaffUser> =>
      toStaffUser(await request('/staff/me', { schema: staffUserSchema, signal })),
    enabled: token !== null,
    // a 401 is an answer, not a hiccup; retrying it just delays the redirect
    retry: false,
    staleTime: 5 * 60_000,
  });
}
